import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState, useRef, useEffect } from 'react'
import { api } from '@/api'
import { useAuthStore, useChatStore } from '@/stores'
import type { ChatMessage, LoginCredentials, User } from '@/types'
import { STORAGE_KEYS } from '@/consts'
import { generateId } from '@/utils'

// Auth hooks
export function useLogin() {
  const queryClient = useQueryClient()
  const setAuth = useAuthStore((state) => state.setAuth)

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const response = await api.login(credentials)
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.access_token)
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refresh_token)
      setAuth({
        user: response.user,
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      })
      return response
    },
  })
}

export function useLogout() {
  const logout = useAuthStore((state) => state.logout)
  const queryClient = useQueryClient()

  return useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
    logout()
    queryClient.clear()
  }, [logout, queryClient])
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.getMe(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

// Chat hooks
export function useChatStream() {
  const [streamingContent, setStreamingContent] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const lastProcessedContentRef = useRef<string>('')
  const isStreamAbortedRef = useRef<boolean>(false)

  const { addMessage, updateMessage, currentSessionId, setIsStreaming: setStoreIsStreaming } = useChatStore()

  const sendMessage = useCallback(
    async (message: string, history: { role: string; content: string }[] = []) => {
      if (!currentSessionId) return

      // Prevent concurrent streams - if already streaming, abort previous first
      if (isStreaming) {
        abortControllerRef.current?.abort()
      }

      setIsStreaming(true)
      setStoreIsStreaming(true)
      setStreamingContent('')
      lastProcessedContentRef.current = ''
      isStreamAbortedRef.current = false

      // Generate unique IDs using generateId
      const userMessageId = generateId()
      const assistantMessageId = generateId()

      // Add user message
      const userMessage: ChatMessage = {
        id: userMessageId,
        role: 'user',
        content: message,
        timestamp: new Date().toISOString(),
      }
      addMessage(currentSessionId, userMessage)

      // Add empty assistant message
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true,
      }
      addMessage(currentSessionId, assistantMessage)

      try {
        abortControllerRef.current = new AbortController()

        const response = await fetch(`${import.meta.env.VITE_API_URL || '/api/v1'}/agent/chat/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || ''}`,
          },
          body: JSON.stringify({ message, history, stream: true }),
          signal: abortControllerRef.current.signal,
        })

        if (!response.ok) {
          throw new Error('Chat request failed')
        }

        const reader = response.body?.getReader()
        if (!reader) throw new Error('No response body')

        const decoder = new TextDecoder()
        let rawContent = ''
        let sseBuffer = ''

        const processContentChunk = (rawChunk: string) => {
          rawContent += rawChunk
          if (rawContent === lastProcessedContentRef.current) {
            return
          }

          lastProcessedContentRef.current = rawContent
          setStreamingContent(rawContent)
          updateMessage(currentSessionId, assistantMessageId, {
            content: rawContent,
          })
        }

        const processSseEvent = (eventBlock: string) => {
          const dataLines = eventBlock
            .split(/\r?\n/)
            .filter((line) => line.startsWith('data:'))
            .map((line) => line.slice(5).replace(/^ /, ''))

          if (dataLines.length === 0) {
            return
          }

          const data = dataLines.join('\n')
          if (data === '[DONE]') {
            return
          }

          try {
            const parsed = JSON.parse(data)

            if (typeof parsed === 'string') {
              processContentChunk(parsed)
              return
            }

            if (typeof parsed?.data === 'string') {
              processContentChunk(parsed.data)
              return
            }
          } catch {
            processContentChunk(data)
            return
          }

          processContentChunk(data)
        }

        while (true) {
          const { done, value } = await reader.read()
          if (done || isStreamAbortedRef.current) break

          sseBuffer += decoder.decode(value, { stream: true })
          const eventBlocks = sseBuffer.split(/\r?\n\r?\n/)
          sseBuffer = eventBlocks.pop() || ''

          for (const eventBlock of eventBlocks) {
            if (isStreamAbortedRef.current) {
              break
            }
            processSseEvent(eventBlock)
          }
        }

        const remainingChunk = decoder.decode()
        if (remainingChunk) {
          sseBuffer += remainingChunk
        }

        if (sseBuffer.trim()) {
          processSseEvent(sseBuffer)
        }

        updateMessage(currentSessionId, assistantMessageId, {
          content: rawContent,
          isStreaming: false,
        })
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('Chat aborted')
          isStreamAbortedRef.current = true
        } else {
          console.error('Chat error:', error)
          // Abort the stream and mark as aborted to prevent further updates
          abortControllerRef.current?.abort()
          isStreamAbortedRef.current = true
          updateMessage(currentSessionId, assistantMessageId, {
            content: '抱歉，发生了错误。请稍后重试。',
            isStreaming: false,
          })
        }
      } finally {
        setIsStreaming(false)
        setStoreIsStreaming(false)
        setStreamingContent('')
        lastProcessedContentRef.current = ''
      }
    },
    [currentSessionId, addMessage, updateMessage, setStoreIsStreaming]
  )

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }, [])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return { sendMessage, streamingContent, isStreaming, abort }
}

// Users hooks
export function useUsers(params?: { page?: number; page_size?: number; search?: string }) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => api.getUsers(params),
    staleTime: 30 * 1000,
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()
  const updateUser = useAuthStore((state) => state.updateUser)

  return useMutation({
    mutationFn: (data: Partial<User>) => api.updateMe(data),
    onSuccess: (data) => {
      updateUser(data)
      queryClient.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

// Attendance hooks
export function useAttendanceStats(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: ['attendance', 'stats', startDate, endDate],
    queryFn: () => api.getAttendanceStats(startDate, endDate),
    staleTime: 5 * 60 * 1000,
  })
}

export function useAttendanceCalendar(year: number, month: number) {
  return useQuery({
    queryKey: ['attendance', 'calendar', year, month],
    queryFn: () => api.getAttendanceCalendar(year, month),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCheckIn() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (location?: string) => api.checkIn(location),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] })
    },
  })
}

export function useCheckOut() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (location?: string) => api.checkOut(location),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendance'] })
    },
  })
}

// Leave hooks
export function useLeaveBalance() {
  return useQuery({
    queryKey: ['leave', 'balance'],
    queryFn: () => api.getLeaveBalance(),
    staleTime: 5 * 60 * 1000,
  })
}

export function useLeaveRequests(params?: { page?: number; page_size?: number; status?: string }) {
  return useQuery({
    queryKey: ['leave', 'requests', params],
    queryFn: () => api.getLeaveRequests(params),
    staleTime: 30 * 1000,
  })
}

export function useCreateLeaveRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.createLeaveRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] })
    },
  })
}

export function useCancelLeaveRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.cancelLeaveRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leave'] })
    },
  })
}

// Performance hooks
export function usePerformanceReviews(params?: { page?: number; page_size?: number; year?: number }) {
  return useQuery({
    queryKey: ['performance', 'reviews', params],
    queryFn: () => api.getPerformanceReviews(params),
    staleTime: 30 * 1000,
  })
}

export function usePerformanceStats(year?: number) {
  return useQuery({
    queryKey: ['performance', 'stats', year],
    queryFn: () => api.getPerformanceStats(year),
    staleTime: 5 * 60 * 1000,
  })
}

// Documents hooks
export function useFolders() {
  return useQuery({
    queryKey: ['documents', 'folders'],
    queryFn: () => api.getFolders(),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateFolder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ name, description, parentId }: { name: string; description?: string; parentId?: number }) =>
      api.createFolder(name, description, parentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', 'folders'] })
    },
  })
}

export function useDocuments(params?: { page?: number; page_size?: number; folder_id?: number; search?: string }) {
  return useQuery({
    queryKey: ['documents', params],
    queryFn: () => api.getDocuments(params),
    staleTime: 30 * 1000,
  })
}

export function useUploadDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ file, title, folderId, isPublic }: { file: File; title?: string; folderId?: number; isPublic?: boolean }) =>
      api.uploadDocument(file, title, folderId, isPublic),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })
}

// Employee hooks
export function useEmployees(params?: {
  page?: number
  page_size?: number
  dept_code?: string | null
  level_code?: string | null
  status?: string | null
  manager_id?: string | null
  search?: string | null
}) {
  return useQuery({
    queryKey: ['employees', params],
    queryFn: () => api.getEmployees(params),
    staleTime: 30 * 1000,
  })
}

export function useEmployee(employeeId: string) {
  return useQuery({
    queryKey: ['employees', employeeId],
    queryFn: () => api.getEmployee(employeeId),
    enabled: !!employeeId,
    staleTime: 30 * 1000,
  })
}

export function useCreateEmployee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: import('@/types').EmployeeCreate) => api.createEmployee(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
    },
  })
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ employeeId, data }: { employeeId: string; data: any }) =>
      api.updateEmployee(employeeId, data),
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
      queryClient.invalidateQueries({ queryKey: ['employees', employeeId] })
    },
  })
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (employeeId: string) => api.deleteEmployee(employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] })
    },
  })
}

export function useSubordinates(employeeId: string) {
  return useQuery({
    queryKey: ['employees', employeeId, 'subordinates'],
    queryFn: () => api.getSubordinates(employeeId),
    enabled: !!employeeId,
    staleTime: 30 * 1000,
  })
}

export function useLevels() {
  return useQuery({
    queryKey: ['levels'],
    queryFn: () => api.getLevels(),
    staleTime: 5 * 60 * 1000,
  })
}

export function useDepartments() {
  return useQuery({
    queryKey: ['departments'],
    queryFn: () => api.getDepartments(),
    staleTime: 5 * 60 * 1000,
  })
}

// Work History hooks
export function useWorkHistory(employeeId: string) {
  return useQuery({
    queryKey: ['employees', employeeId, 'work-history'],
    queryFn: () => api.getWorkHistory(employeeId),
    enabled: !!employeeId,
    staleTime: 30 * 1000,
  })
}

export function useCreateWorkHistory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ employeeId, data }: { employeeId: string; data: any }) =>
      api.createWorkHistory(employeeId, data),
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: ['employees', employeeId, 'work-history'] })
    },
  })
}

export function useUpdateWorkHistory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ employeeId, historyId, data }: { employeeId: string; historyId: number; data: any }) =>
      api.updateWorkHistory(employeeId, historyId, data),
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: ['employees', employeeId, 'work-history'] })
    },
  })
}

export function useDeleteWorkHistory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ employeeId, historyId }: { employeeId: string; historyId: number }) =>
      api.deleteWorkHistory(employeeId, historyId),
    onSuccess: (_, { employeeId }) => {
      queryClient.invalidateQueries({ queryKey: ['employees', employeeId, 'work-history'] })
    },
  })
}
